import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../config';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_no-sub-pdf/artifacts/8avttwow_logohexoworkssanstitre.png";

const labels = {
  en: {
    title: 'Set a new password',
    subtitle: 'Choose a strong password for your account.',
    password: 'New password',
    confirm: 'Confirm password',
    submit: 'Reset password',
    success_title: 'Password updated!',
    success_text: 'Your password has been reset. You can now log in.',
    login: 'Go to login',
    error_mismatch: 'Passwords do not match.',
    error_short: 'Password must be at least 6 characters.',
    back: 'Back to login',
  },
  fr: {
    title: 'Nouveau mot de passe',
    subtitle: 'Choisissez un mot de passe fort pour votre compte.',
    password: 'Nouveau mot de passe',
    confirm: 'Confirmer le mot de passe',
    submit: 'Réinitialiser',
    success_title: 'Mot de passe mis à jour !',
    success_text: 'Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.',
    login: 'Se connecter',
    error_mismatch: 'Les mots de passe ne correspondent pas.',
    error_short: 'Le mot de passe doit contenir au moins 6 caractères.',
    back: 'Retour à la connexion',
  },
  hi: {
    title: 'नया पासवर्ड सेट करें',
    subtitle: 'अपने खाते के लिए एक मजबूत पासवर्ड चुनें।',
    password: 'नया पासवर्ड',
    confirm: 'पासवर्ड की पुष्टि करें',
    submit: 'पासवर्ड रीसेट करें',
    success_title: 'पासवर्ड अपडेट हो गया!',
    success_text: 'आपका पासवर्ड रीसेट हो गया है। अब आप लॉग इन कर सकते हैं।',
    login: 'लॉग इन करें',
    error_mismatch: 'पासवर्ड मेल नहीं खाते।',
    error_short: 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए।',
    back: 'लॉगिन पर वापस जाएं',
  },
};

export default function ResetPasswordPage() {
  const { lang } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const l = labels[lang] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(l.error_short); return; }
    if (password !== confirm) { toast.error(l.error_mismatch); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Lien invalide ou expiré.' : 'Invalid or expired link.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-8 transition-colors" data-testid="reset-back-link">
          <ArrowLeft className="w-4 h-4" />
          {l.back}
        </Link>

        <div className="flex items-center gap-2.5 mb-8">
          <img src={LOGO_ICON} alt="HexoPDF" className="w-8 h-8" />
          <span className="font-heading font-bold text-xl text-foreground">Hexo<span className="text-blue-500">PDF</span></span>
        </div>

        {success ? (
          <div data-testid="reset-success">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">{l.success_title}</h1>
            <p className="text-muted-foreground text-sm mb-6">{l.success_text}</p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 font-medium"
              data-testid="reset-login-btn"
            >
              {l.login}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-foreground mb-1" data-testid="reset-title">{l.title}</h1>
            <p className="text-muted-foreground text-sm mb-8">{l.subtitle}</p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">{l.password}</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-card border-border focus:border-blue-500 text-foreground h-11 rounded-lg"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">{l.confirm}</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="bg-card border-border focus:border-blue-500 text-foreground h-11 rounded-lg"
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 font-medium shadow-[0_0_15px_rgba(59,130,246,0.4)] hover:shadow-[0_0_25px_rgba(59,130,246,0.6)] transition-all"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : l.submit}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
